import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import DangKyOnline from './components/DangKyOnline';
import DangKyThanhCong from './components/DangKyThanhCong';
import Dashboard from './components/Dashboard';
import ReportTab from './components/ReportTab';
import HSBAPage from './components/HSBAPage';
import TimekeepingTab from './components/TimekeepingTab';
import TimeManagementTab from './components/TimeManagementTab';
import DutyScheduleTab from './components/DutyScheduleTab';
import ViewDutyScheduleTab from './components/ViewDutyScheduleTab';
import SoDiBuong from './components/SoDiBuong'; // Assuming this path
import ChamCongKhoaPhongTab from './components/ChamCongKhoaPhongTab';
import DangKyOnlineManagement from './components/DangKyOnlineManagement';
import ChiSoTongHop from './components/ChiSoTongHop';
import CongViecPage from './components/CongViecPage';
import QuanLyCongViecPage from './components/QuanLyCongViecPage';
import PharmacyManagement from './components/PharmacyManagement';
import UserManagement from './components/UserManagement';
import UserProfile from './components/UserProfile';
import ChangePassword from './components/ChangePassword';
import MainLayout from './components/MainLayout';
import LichSuCls from './components/LichSuCls';
import CskhTab from './components/CskhTab';
import { ConfigProvider, Layout, Menu, Button, Avatar, Dropdown, Space, Grid, Drawer } from 'antd';
import viVN from 'antd/locale/vi_VN';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  FileTextOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  MenuOutlined,
  SyncOutlined,
  MedicineBoxOutlined,
  ClockCircleOutlined,
  ContainerOutlined,
  SolutionOutlined,
  HeartOutlined
} from '@ant-design/icons';
import './components/Dashboard.css';
import { authService } from './services/authService';
import HisSyncTab from './components/HisSyncTab'; // New import
import HoSoNhanVienManagement from './components/HoSoNhanVienManagement';

const { Header, Sider } = Layout;
const { useBreakpoint } = Grid;

// Helper to check permissions
const hasPermission = (permission: string) => {
    const storedPermissions = localStorage.getItem('permissions');
    
    if (!storedPermissions || storedPermissions === 'undefined') {
        return false;
    }
    try {
        const permissionsList: string[] = JSON.parse(storedPermissions);
        const result = permissionsList.includes(permission);
        return result;
    } catch (e) {
        console.error("Failed to parse permissions from localStorage", e);
        return false;
    }
};

// Protected Route Component
const ProtectedRoute: React.FC<{ permission: string; children: React.ReactElement }> = ({ permission, children }) => {
    if (!hasPermission(permission)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

const baseMenuItems = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    requiredPermission: null, // Always visible
  },
  {
    key: 'users',
    icon: <TeamOutlined />,
    label: 'Quản lý người dùng',
    requiredPermission: 'ManageUsers',
  },
  {
    key: 'reports',
    icon: <BarChartOutlined />,
    label: 'Báo cáo',
    requiredPermission: 'ViewReports',
  },
  {
    key: 'hsba',
    icon: <FileTextOutlined />,
    label: 'Hồ sơ bệnh án',
    requiredPermission: 'HSBA',
  },
  {
    key: 'timekeeping',
    icon: <FileTextOutlined />,
    label: 'Kiểm tra chấm công',
    requiredPermission: 'CheckMcc',
  },
  {
    key: 'duty-schedule',
    icon: <CalendarOutlined />,
    label: 'Quản lý lịch trực',
    requiredPermission: 'ManageDutySchedule', // Placeholder permission
  },
  {
    key: 'view-duty-schedule', // New key
    icon: <CalendarOutlined />, // Reusing icon for now, can change later
    label: 'Xem lịch trực',
    requiredPermission: 'ViewSchedule',
  },
  {
    key: 'sodibuong-dt',
    icon: <FileTextOutlined />,
    label: 'Sổ đi buồng (ĐT)',
    requiredPermission: 'Sodibuong',
  },
  {
    key: 'sodibuong-khac',
    icon: <FileTextOutlined />,
    label: 'Sổ đi buồng (Khác)',
    requiredPermission: 'Sodibuongcn',
  },
  {
    key: 'dangkyonline',
    icon: <UserOutlined />, // Using UserOutlined for now
    label: 'Đăng ký online',
    requiredPermission: 'DangKyOnline',
  },
  {
    key: 'chisotonghop',
    icon: <BarChartOutlined />,
    label: 'Danh mục chỉ số',
    requiredPermission: 'ChiSoTongHop',
  },
  {
    key: 'congviec',
    icon: <ContainerOutlined />, // Example icon, needs import
    label: 'Công việc',
    requiredPermission: 'CongViec',
  },
  {
    key: 'quanlycongviec',
    icon: <SolutionOutlined />, // Example icon, needs import
    label: 'Quản lý công việc',
    requiredPermission: 'QuanLyCongViec',
  },
  {
    key: 'pharmacy',
    icon: <MedicineBoxOutlined />,
    label: 'Quản lý Dược',
    requiredPermission: 'Duoc',
  },
  {
    key: 'cskh',
    icon: <HeartOutlined />,
    label: 'Chăm sóc khách hàng',
    requiredPermission: 'Cskh',
  },
  {
    key: 'chamcongkhoaphong',
    icon: <FileTextOutlined />,
    label: 'Chấm công khoa phòng',
    requiredPermission: 'Chamcongkhoaphong',
  },
  {
    key: 'hosonhanvien',
    icon: <SolutionOutlined />,
    label: 'Hồ sơ nhân viên',
    requiredPermission: 'HoSoNhanVien',
  },
  {
    key: 'zk-time-management',
    icon: <ClockCircleOutlined />,
    label: 'Quản lý Máy chấm công',
    requiredPermission: 'QuanlyMCC',
  },
  {
    key: 'documents',
    icon: <FileTextOutlined />,
    label: 'Tài liệu',
    requiredPermission: null, // Always visible for now
  },
  {
    key: 'settings',
    icon: <SettingOutlined />,
    label: 'Cài đặt hệ thống',
    requiredPermission: 'Settings', // Change to 'Settings' permission
  },
];

const AppLayout: React.FC<{ children: React.ReactNode; userMenuItems: any[]; currentUserName: string | null }> = ({ children, userMenuItems, currentUserName }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const isMobile = !screens.md;

  // Filter menu items based on permissions
  const filteredMenuItems = baseMenuItems.filter(item => {
    return !item.requiredPermission || hasPermission(item.requiredPermission);
  });

  // Remove custom props before rendering
  const menuProps = filteredMenuItems.map(({ requiredPermission, ...rest }) => rest);

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'dashboard') navigate('/dashboard');
    else if (key === 'reports') navigate('/reports');
    else if (key === 'users') navigate('/users');
    else if (key === 'hsba') navigate('/hsba');
    else if (key === 'timekeeping') navigate('/timekeeping'); // Handle timekeeping navigation
    else if (key === 'duty-schedule') navigate('/duty-schedule');
    else if (key === 'view-duty-schedule') navigate('/view-duty-schedule');
    else if (key === 'sodibuong-dt') navigate('/sodibuong-dt');
    else if (key === 'sodibuong-khac') navigate('/sodibuong-khac');
    else if (key === 'dangkyonline') navigate('/dangkyonline');
    else if (key === 'chisotonghop') navigate('/chi-so-tong-hop');
    else if (key === 'congviec') navigate('/cong-viec');
    else if (key === 'quanlycongviec') navigate('/quan-ly-cong-viec');
    else if (key === 'pharmacy') navigate('/pharmacy');
    else if (key === 'cskh') navigate('/cskh');
    else if (key === 'chamcongkhoaphong') navigate('/cham-cong-khoa-phong');
    else if (key === 'hosonhanvien') navigate('/ho-so-nhan-vien');
    else if (key === 'zk-time-management') navigate('/zk-time-management');
    else if (key === 'documents') navigate('/documents');
    else if (key === 'settings') navigate('/settings'); // Navigate to /settings for the Settings tab
    else if (key === 'profile') navigate('/profile');
    else if (key === 'change-password') navigate('/change-password'); // Handle change-password navigation
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  const selectedMenu = location.pathname.includes('report')
    ? 'reports'
    : location.pathname.includes('dashboard')
    ? 'dashboard'
    : location.pathname.includes('users')
    ? 'users'
    : location.pathname.includes('hsba')
    ? 'hsba'
    : location.pathname.includes('timekeeping')
    ? 'timekeeping'
    : location.pathname.includes('view-duty-schedule')
    ? 'view-duty-schedule'
    : location.pathname.includes('sodibuong-dt')
    ? 'sodibuong-dt'
    : location.pathname.includes('sodibuong-khac')
    ? 'sodibuong-khac'
    : location.pathname.includes('dangkyonline')
    ? 'dangkyonline'
    : location.pathname.includes('chi-so-tong-hop')
    ? 'chisotonghop'
    : location.pathname.includes('duty-schedule')
    ? 'duty-schedule'
    : location.pathname.includes('pharmacy')
    ? 'pharmacy'
    : location.pathname.includes('cskh')
    ? 'cskh'
    : location.pathname.includes('ho-so-nhan-vien')
    ? 'hosonhanvien'
    : location.pathname.includes('zk-time-management')
    ? 'zk-time-management'
    : location.pathname.includes('documents')
    ? 'documents'
    : location.pathname.includes('settings')
    ? 'settings'
    : location.pathname.includes('profile')
    ? 'profile'
    : location.pathname.includes('change-password')
    ? 'change-password'
    : '';

  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedMenu]}
      onClick={handleMenuClick}
      items={menuProps} // Use sanitized props
    />
  );

  return (
    <MainLayout
      sider={
        !isMobile && (
          <Sider trigger={null} collapsible collapsed={collapsed}>
            <div className="dashboard-logo">Phủ Diễn</div>
            {menu}
          </Sider>
        )
      }
      header={
        <Header className="dashboard-header">
          <Button
            type="text"
            icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setDrawerVisible(!drawerVisible) : setCollapsed(!collapsed)}
            className="dashboard-toggle-button"
          />
          <Space>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" className="dashboard-user-button">
                <Space>
                  <Avatar icon={<UserOutlined />} />
                  <span>{currentUserName || 'Loading...'}</span>
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>
      }
      contentClassName={selectedMenu === 'reports' ? '' : 'dashboard-content'}
      contentStyle={selectedMenu === 'reports' ? { padding: 0, margin: 0, background: '#23293a', minHeight: '100vh', borderRadius: 0 } : {}}
    >
      {children}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          closable={false}
          bodyStyle={{ padding: 0, background: '#001529' }}
          width={250}
        >
          <div className="dashboard-logo" style={{ color: 'white', paddingLeft: '24px' }}>PHỦ DIỄN</div>
          {menu}
        </Drawer>
      )}
    </MainLayout>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [currentUserName, setCurrentUserName] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch user profile on mount or when authenticated state changes
  useEffect(() => {
    // Các route public không cần hiển thị tên user trên header, bỏ qua gọi /auth/me (tránh request dư gây 401)
    const publicPaths = ['/lich-su-cls/', '/dang-ky-kham-tu-xa', '/dang-ky-thanh-cong'];
    const currentPath = window.location.pathname;
    const isOnPublicPage = publicPaths.some(p => currentPath.startsWith(p));

    if (isAuthenticated && !isOnPublicPage) {
      const fetchUserName = async () => {
        try {
          const profile = await authService.getProfile();
          setCurrentUserName(profile.employeeName || profile.username);
        } catch (error) {
          console.error("Failed to fetch user name for header:", error);
          setCurrentUserName("User"); // Fallback
        }
      };
      fetchUserName();
    } else {
      setCurrentUserName(null);
    }
  }, [isAuthenticated]);

  const handleLogin = (response: any) => {
    console.log('Login response:', response);
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken);
    console.log('Setting permissions in localStorage:', response.permissions);
    localStorage.setItem('permissions', JSON.stringify(response.permissions));
    setIsAuthenticated(true);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('permissions');
    setIsAuthenticated(false);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Hồ sơ cá nhân',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: 'Đổi mật khẩu',
      onClick: () => navigate('/change-password'), // Add onClick handler
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      onClick: handleLogout,
    },
  ];

  return (
    <Routes>
      <Route path="/dang-ky-kham-tu-xa" element={<DangKyOnline />} />
      <Route path="/dang-ky-thanh-cong" element={<DangKyThanhCong />} />
      <Route path="/lich-su-cls/:token" element={<LichSuCls />} />
      {!isAuthenticated ? (
        <>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <Route
          path="/*"
          element={
            <AppLayout userMenuItems={userMenuItems} currentUserName={currentUserName}>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="reports" element={<ReportTab />} />
                <Route path="hsba" element={
                  <ProtectedRoute permission="HSBA">
                    <HSBAPage />
                  </ProtectedRoute>
                } />
                <Route path="timekeeping" element={
                  <ProtectedRoute permission="CheckMcc">
                    <TimekeepingTab />
                  </ProtectedRoute>
                } />
                <Route path="duty-schedule" element={
                  <ProtectedRoute permission="ManageDutySchedule">
                    <DutyScheduleTab />
                  </ProtectedRoute>
                } />
                <Route path="view-duty-schedule" element={
                  <ProtectedRoute permission="ViewSchedule">
                    <ViewDutyScheduleTab />
                  </ProtectedRoute>
                } />
                <Route path="sodibuong-dt" element={
                  <ProtectedRoute permission="Sodibuong">
                    <SoDiBuong loai={1} />
                  </ProtectedRoute>
                } />
                <Route path="sodibuong-khac" element={
                  <ProtectedRoute permission="Sodibuongcn">
                    <SoDiBuong loai={2} />
                  </ProtectedRoute>
                } />
                <Route path="dangkyonline" element={
                  <ProtectedRoute permission="DangKyOnline">
                    <DangKyOnlineManagement />
                  </ProtectedRoute>
                } />
                <Route path="chi-so-tong-hop" element={
                  <ProtectedRoute permission="ChiSoTongHop">
                    <ChiSoTongHop />
                  </ProtectedRoute>
                } />
                <Route path="cong-viec" element={
                  <ProtectedRoute permission="CongViec">
                    <CongViecPage />
                  </ProtectedRoute>
                } />
                <Route path="quan-ly-cong-viec" element={
                  <ProtectedRoute permission="QuanLyCongViec">
                    <QuanLyCongViecPage />
                  </ProtectedRoute>
                } />
                <Route path="pharmacy" element={
                  <ProtectedRoute permission="Duoc">
                    <PharmacyManagement />
                  </ProtectedRoute>
                } />
                <Route path="cskh" element={
                  <ProtectedRoute permission="Cskh">
                    <CskhTab />
                  </ProtectedRoute>
                } />
                <Route path="cham-cong-khoa-phong" element={
                  <ProtectedRoute permission="Chamcongkhoaphong">
                    <ChamCongKhoaPhongTab />
                  </ProtectedRoute>
                } />
                <Route path="ho-so-nhan-vien" element={
                  <ProtectedRoute permission="HoSoNhanVien">
                    <HoSoNhanVienManagement />
                  </ProtectedRoute>
                } />
                <Route path="zk-time-management" element={
                  <ProtectedRoute permission="QuanlyMCC">
                    <TimeManagementTab />
                  </ProtectedRoute>
                } />
                <Route path="users" element={<ProtectedRoute permission="ManageUsers"><UserManagement /></ProtectedRoute>} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="change-password" element={<ChangePassword />} /> {/* Add the new change-password route */}
                <Route path="settings" element={
                  <ProtectedRoute permission="Settings">
                    <HisSyncTab />
                  </ProtectedRoute>
                } />
                <Route path="/" element={<Navigate to="dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AppLayout>
          }
        />
      )}
    </Routes>
  );
};

const AppWrapper: React.FC = () => (
  <ConfigProvider locale={viVN}>
    <Router>
      <App />
    </Router>
  </ConfigProvider>
);

export default AppWrapper;